import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Box, Modal, Checkbox } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { getAllTickets } from '../../../api/ticket';
import { getIndividualTicketCandidateMembers, getTrainerAssignedTicketIds } from '../../../api/salarySetting';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommaInput from './CommaInput';
import {
  Card,
  SectionTitle,
  SectionTitleRow,
  SectionContent,
  FormRow,
  FormFieldGroup,
  FormFieldLabel,
  FormFieldDesc,
  Divider,
  ToggleSwitch,
  ToggleFieldRow,
  FieldBlock,
  RadioRow,
  RadioItem,
  RadioCircle,
  RadioDot,
  RadioLabel,
  LessonLabel,
  AmountRow,
  AmountItem,
  AmountLabel,
  InputUnit,
  IndividualHeader,
  IndividualHeaderText,
  IndividualBadge,
  TableWrapper,
  TableHeaderRow,
  TableHeaderCell,
  TableHeaderText,
  TableBodyRow,
  TableBodyCellRow,
  TableBodyCell,
  TableCellText,
  SearchField,
  InputBoxText,
  RangeRow,
  RangeInputBox,
  RangeSeparator,
  AddRangeButton,
  AddRangeText,
  UnitSelect,
  RealInput,
  HelpTooltip,
  ErrorFieldWrapper,
  ErrorText,
  EmptyRangeHint,
  NoteText,
  CheckRow,
  CheckLabel,
  CheckboxCircle,
} from './SalarySettingStyled';

// ============================================
// 개인레슨 기본 수당
// ============================================
const PrivateLessonBasicSection = ({ settings, onSettingsChange, validationError }) => {
  const { t } = useTranslation();
  const enabled = settings.privateLessonBasicEnabled === 'Y';

  return (
    <FieldBlock>
      <FormRow sx={{ alignItems: 'flex-start' }}>
        <FormFieldGroup>
          <ToggleFieldRow>
            <FormFieldLabel>{t('개인레슨 기본 수당')}</FormFieldLabel>
            <ToggleSwitch
              checked={enabled}
              onChange={() => onSettingsChange('privateLessonBasicEnabled', enabled ? 'N' : 'Y')}
            />
          </ToggleFieldRow>
          <FormFieldDesc>{t('개인레슨 수업당 강사에게 지급되는 수당')}</FormFieldDesc>
        </FormFieldGroup>

        {enabled && (
          <FieldBlock sx={{ flex: 1 }}>
            {/* 부가세 여부 */}
            <RadioRow sx={{ justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LessonLabel>{t('부가세')}</LessonLabel>
                <HelpTooltip title={"포함: 수강권 금액을 그대로 사용하여 계산\n미포함: 수강권 금액에서 부가세(10%)를 제외한 금액으로 계산\n\n예) 수강권 110만원\n포함 → 110만원 기준\n미포함 → 100만원 기준"} />
              </Box>
              <RadioItem onClick={() => onSettingsChange('privateLessonVatIncluded', 'Y')}>
                <RadioCircle isSelected={settings.privateLessonVatIncluded === 'Y'}>
                  {settings.privateLessonVatIncluded === 'Y' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('포함')}</RadioLabel>
              </RadioItem>
              <RadioItem onClick={() => onSettingsChange('privateLessonVatIncluded', 'N')}>
                <RadioCircle isSelected={settings.privateLessonVatIncluded !== 'Y'}>
                  {settings.privateLessonVatIncluded !== 'Y' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('미포함')}</RadioLabel>
              </RadioItem>
            </RadioRow>

            <Divider />

            {/* 수당 기준 */}
            <RadioRow sx={{ justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LessonLabel required>{t('수당 기준')}</LessonLabel>
                <HelpTooltip title={"판매금액: 수강권의 정가 기준으로 회당 단가 계산\n결제금액: 회원이 실제 결제한 금액 기준으로 회당 단가 계산\n\n할인 적용 시 두 금액이 다를 수 있습니다.\n예) 정가 100만원, 할인 후 80만원 결제\n판매금액 → 100만원 ÷ 횟수\n결제금액 → 80만원 ÷ 횟수"} />
              </Box>
              <RadioItem onClick={() => onSettingsChange('privateLessonBasis', 'SALE_PRICE')}>
                <RadioCircle isSelected={settings.privateLessonBasis === 'SALE_PRICE'}>
                  {settings.privateLessonBasis === 'SALE_PRICE' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('판매금액')}</RadioLabel>
              </RadioItem>
              <RadioItem onClick={() => onSettingsChange('privateLessonBasis', 'PAID_PRICE')}>
                <RadioCircle isSelected={settings.privateLessonBasis === 'PAID_PRICE'}>
                  {settings.privateLessonBasis === 'PAID_PRICE' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('결제금액')}</RadioLabel>
              </RadioItem>
            </RadioRow>

            <Divider />

            {/* 퍼센트 안내 — 개인레슨은 분모가 "회당 단가"다.
                그룹레슨은 분모가 기본 급여액이라 규칙이 달라, 예전처럼 섹션 최상단에 두면
                그룹레슨에도 적용되는 것처럼 읽힌다. 그래서 각 블록 안으로 나눠 넣었다. */}
            <NoteText>
              {t('※ 퍼센트로 설정 시 "수강권의 가격(판매금액or결제금액)" 나누기 "수업 횟수"로 계산한 회당 단가에서 설정한 퍼센트만큼 산정됩니다')}
            </NoteText>

            <Divider />

            {/* 재등록 별도 설정 */}
            <RadioRow sx={{ justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LessonLabel>{t('재등록 별도')}</LessonLabel>
                <HelpTooltip title={"ON: 이 지점에서 처음 결제한 회원(신규)과 재결제 회원(재등록)에 다른 수당을 적용합니다.\nOFF: 모든 회원에게 신규 수당을 동일하게 적용합니다."} />
              </Box>
              <ToggleSwitch
                checked={settings.privateLessonRenewSeparate === 'Y'}
                onChange={() => onSettingsChange('privateLessonRenewSeparate', settings.privateLessonRenewSeparate === 'Y' ? 'N' : 'Y')}
              />
            </RadioRow>

            <Divider />

            {/* 신규 / 재등록 */}
            <AmountRow>
              <AmountItem>
                <AmountLabel>{t('신규')}</AmountLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <ErrorFieldWrapper hasError={validationError?.field === 'privateLessonNewAmount'} data-field="privateLessonNewAmount">
                    <CommaInput
                      inputWidth="140px"
                      value={settings.privateLessonNewAmount}
                      onChange={val => onSettingsChange('privateLessonNewAmount', val)}
                    />
                  </ErrorFieldWrapper>
                  {validationError?.field === 'privateLessonNewAmount' && <ErrorText>{t('신규 수당을 입력해주세요')}</ErrorText>}
                </Box>
                <UnitSelect
                  value={settings.privateLessonNewUnit || '원'}
                  onChange={e => onSettingsChange('privateLessonNewUnit', e.target.value)}
                >
                  <option value="원">원</option>
                  <option value="%">%</option>
                </UnitSelect>
              </AmountItem>
              {settings.privateLessonRenewSeparate === 'Y' && (
                <AmountItem>
                  <AmountLabel>{t('재등록')}</AmountLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <ErrorFieldWrapper hasError={validationError?.field === 'privateLessonRenewAmount'} data-field="privateLessonRenewAmount">
                      <CommaInput
                        inputWidth="140px"
                        value={settings.privateLessonRenewAmount}
                        onChange={val => onSettingsChange('privateLessonRenewAmount', val)}
                      />
                    </ErrorFieldWrapper>
                    {validationError?.field === 'privateLessonRenewAmount' && <ErrorText>{t('재등록 수당을 입력해주세요')}</ErrorText>}
                  </Box>
                  <UnitSelect
                    value={settings.privateLessonRenewUnit || '원'}
                    onChange={e => onSettingsChange('privateLessonRenewUnit', e.target.value)}
                  >
                    <option value="원">원</option>
                    <option value="%">%</option>
                  </UnitSelect>
                </AmountItem>
              )}
            </AmountRow>

            <Divider />

            {/* 차감된 결석 */}
            <RadioRow sx={{ justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LessonLabel required>{t('차감된 결석')}</LessonLabel>
                <HelpTooltip title={"회원이 결석(차감)했을 때 수당 처리 방식입니다.\n\n미정산: 결석 수업은 수당을 지급하지 않음\n정산: 출석과 동일하게 수당 지급\n직접입력: 결석 시 별도 금액 지급 (원 또는 회당단가의 %)"} />
              </Box>
              <RadioItem onClick={() => onSettingsChange('privateLessonAbsence', 'NOT_SETTLED')}>
                <RadioCircle isSelected={settings.privateLessonAbsence === 'NOT_SETTLED'}>
                  {settings.privateLessonAbsence === 'NOT_SETTLED' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('미정산')}</RadioLabel>
              </RadioItem>
              <RadioItem onClick={() => onSettingsChange('privateLessonAbsence', 'SETTLED')}>
                <RadioCircle isSelected={settings.privateLessonAbsence === 'SETTLED'}>
                  {settings.privateLessonAbsence === 'SETTLED' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('정산')}</RadioLabel>
              </RadioItem>
              <RadioItem onClick={() => onSettingsChange('privateLessonAbsence', 'CUSTOM')}>
                <RadioCircle isSelected={settings.privateLessonAbsence === 'CUSTOM'}>
                  {settings.privateLessonAbsence === 'CUSTOM' && <RadioDot />}
                </RadioCircle>
                <RadioLabel>{t('직접입력')}</RadioLabel>
                {settings.privateLessonAbsence === 'CUSTOM' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CommaInput
                      inputWidth="120px"
                      value={settings.privateLessonAbsenceAmount}
                      onChange={val => onSettingsChange('privateLessonAbsenceAmount', val)}
                    />
                    <UnitSelect
                      value={settings.privateLessonAbsenceUnit || '원'}
                      onChange={e => onSettingsChange('privateLessonAbsenceUnit', e.target.value)}
                    >
                      <option value="원">원</option>
                      <option value="%">%</option>
                    </UnitSelect>
                  </Box>
                )}
              </RadioItem>
            </RadioRow>
          </FieldBlock>
        )}
      </FormRow>
    </FieldBlock>
  );
};

// ============================================
// 개인레슨 개별 수당 모달
// ============================================
const INDIVIDUAL_TICKET_DEFAULTS = {
  lessonBasis: 'SALE_PRICE', vatIncluded: 'N',
  newAmount: 0, newUnit: '원', renewAmount: 0, renewUnit: '원', renewSeparate: 'N',
  absenceType: 'NOT_SETTLED', absenceAmount: 0, absenceUnit: '원',
};

const FIELD_INPUT_STYLE = { height: '30px', padding: '4px 8px', fontSize: '13px' };
const FIELD_SELECT_STYLE = { height: '30px', padding: '4px 6px', fontSize: '13px', width: '54px' };
// 이 모달은 테이블 행 안에 펼쳐지는 좁은 영역이라, 페이지 다른 섹션의 큰 라디오·라벨을
// 그대로 쓰면 답답해 보인다 — sx로만 이 폼 안에서 축소하고 공용 컴포넌트 기본값은 건드리지 않는다.
const COMPACT_LABEL_SX = { width: '68px', minWidth: '68px', fontSize: '13px' };
const COMPACT_ROW_SX = { justifyContent: 'flex-start', height: '30px', gap: '10px' };
const COMPACT_CIRCLE_SX = { width: '16px', height: '16px' };
const COMPACT_DOT_SX = { width: '8px', height: '8px' };
const COMPACT_RADIO_LABEL_SX = { fontSize: '13px' };
const COMPACT_UNIT_SX = { fontSize: '13px' };

// 수당 기준·부가세·수업 수당·결석 입력 폼 — 상품 단위 행/회원 단위 행 모두 같은 필드를 쓴다.
const IndividualTicketFieldsForm = ({ item, onChange }) => {
  const { t } = useTranslation();
  return (
    <>
      {/* 수당 기준 */}
      <RadioRow sx={COMPACT_ROW_SX}>
        <LessonLabel sx={COMPACT_LABEL_SX}>{t('수당 기준')}</LessonLabel>
        {['SALE_PRICE', 'PAID_PRICE'].map(v => (
          <RadioItem key={v} onClick={() => onChange('lessonBasis', v)}>
            <RadioCircle isSelected={item.lessonBasis === v} sx={COMPACT_CIRCLE_SX}>
              {item.lessonBasis === v && <RadioDot sx={COMPACT_DOT_SX} />}
            </RadioCircle>
            <RadioLabel sx={COMPACT_RADIO_LABEL_SX}>{v === 'SALE_PRICE' ? t('판매금액') : t('결제금액')}</RadioLabel>
          </RadioItem>
        ))}
      </RadioRow>

      <Divider />

      {/* 부가세 — 기본 수당의 부가세 설정과 독립적으로 이 상품/회원만 다르게 고를 수 있다 */}
      <RadioRow sx={COMPACT_ROW_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LessonLabel sx={COMPACT_LABEL_SX}>{t('부가세')}</LessonLabel>
          <HelpTooltip title={"위 기본 수당의 부가세 설정과 무관하게 이 상품/회원에만 적용됩니다.\n\n포함: 금액을 그대로 사용하여 계산\n미포함: 금액에서 부가세(10%)를 제외한 금액으로 계산\n\n예) 110만원\n포함 → 110만원 기준\n미포함 → 100만원 기준"} />
        </Box>
        {['Y', 'N'].map(v => (
          <RadioItem key={v} onClick={() => onChange('vatIncluded', v)}>
            <RadioCircle isSelected={(item.vatIncluded || 'N') === v} sx={COMPACT_CIRCLE_SX}>
              {(item.vatIncluded || 'N') === v && <RadioDot sx={COMPACT_DOT_SX} />}
            </RadioCircle>
            <RadioLabel sx={COMPACT_RADIO_LABEL_SX}>{v === 'Y' ? t('포함') : t('미포함')}</RadioLabel>
          </RadioItem>
        ))}
      </RadioRow>

      <Divider />

      {/* 재등록 별도 — 기본 수당 섹션과 동일한 토글. 꺼져 있으면(기본값) 재등록 회원도
          신규 요율을 그대로 쓴다(useNewIndividualRate, SalarySettlementService). */}
      <RadioRow sx={COMPACT_ROW_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LessonLabel sx={COMPACT_LABEL_SX}>{t('재등록 별도')}</LessonLabel>
          <HelpTooltip title={"ON: 신규 회원과 재등록 회원에 다른 수당을 적용합니다.\nOFF: 모든 회원에게 신규 수당을 동일하게 적용합니다."} />
        </Box>
        <ToggleSwitch
          checked={item.renewSeparate === 'Y'}
          onChange={() => onChange('renewSeparate', item.renewSeparate === 'Y' ? 'N' : 'Y')}
        />
      </RadioRow>

      <Divider />

      {/* 수업 수당 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <LessonLabel sx={COMPACT_LABEL_SX}>{t('수업 수당')}</LessonLabel>
        <InputUnit sx={COMPACT_UNIT_SX}>{t('신규')}</InputUnit>
        <CommaInput inputWidth="72px" value={item.newAmount || 0}
          onChange={val => onChange('newAmount', val)} style={FIELD_INPUT_STYLE} />
        <UnitSelect value={item.newUnit || '원'}
          onChange={e => onChange('newUnit', e.target.value)}
          style={FIELD_SELECT_STYLE}>
          <option value="원">원</option>
          <option value="%">%</option>
        </UnitSelect>
        {item.renewSeparate === 'Y' && (
          <>
            <InputUnit sx={{ ...COMPACT_UNIT_SX, ml: '6px' }}>{t('재등록')}</InputUnit>
            <CommaInput inputWidth="72px" value={item.renewAmount || 0}
              onChange={val => onChange('renewAmount', val)} style={FIELD_INPUT_STYLE} />
            <UnitSelect value={item.renewUnit || '원'}
              onChange={e => onChange('renewUnit', e.target.value)}
              style={FIELD_SELECT_STYLE}>
              <option value="원">원</option>
              <option value="%">%</option>
            </UnitSelect>
          </>
        )}
      </Box>
      {item.renewSeparate === 'Y' && !item.renewAmount && (
        <NoteText sx={{ color: '#AD6800', fontSize: '12px' }}>{t('※ 재등록을 비워두면 재등록 회원은 0원이 적용됩니다')}</NoteText>
      )}

      <Divider />

      {/* 차감된 결석 */}
      <RadioRow sx={COMPACT_ROW_SX}>
        <LessonLabel sx={COMPACT_LABEL_SX}>{t('차감된 결석')}</LessonLabel>
        {['NOT_SETTLED', 'SETTLED', 'CUSTOM'].map(type => (
          <RadioItem key={type} onClick={() => onChange('absenceType', type)}>
            <RadioCircle isSelected={item.absenceType === type} sx={COMPACT_CIRCLE_SX}>
              {item.absenceType === type && <RadioDot sx={COMPACT_DOT_SX} />}
            </RadioCircle>
            <RadioLabel sx={COMPACT_RADIO_LABEL_SX}>{type === 'NOT_SETTLED' ? t('미정산') : type === 'SETTLED' ? t('정산') : t('직접입력')}</RadioLabel>
          </RadioItem>
        ))}
        {item.absenceType === 'CUSTOM' && (
          <>
            <CommaInput inputWidth="64px" value={item.absenceAmount || 0}
              onChange={val => onChange('absenceAmount', val)} style={FIELD_INPUT_STYLE} />
            <UnitSelect value={item.absenceUnit || '원'}
              onChange={e => onChange('absenceUnit', e.target.value)}
              style={FIELD_SELECT_STYLE}>
              <option value="원">원</option>
              <option value="%">%</option>
            </UnitSelect>
          </>
        )}
      </RadioRow>
    </>
  );
};

// 회원별 예외 — 이 강사가 담당하는, 이 상품을 보유한 회원 중에서만 고를 수 있다.
// 후보 목록은 펼치자마자 바로 불러오고, 검색은 그 목록 안에서의 필터링이다.
const MemberExceptionSection = ({ courseTicketId, employeeId, branchId, token, memberRows, onAddMember, onRemoveMember, onFieldChange }) => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  const { data: candidates = [] } = useQuery({
    queryKey: ['individualTicketCandidateMembers', employeeId, branchId, courseTicketId],
    queryFn: () => getIndividualTicketCandidateMembers(employeeId, branchId, courseTicketId, token),
    enabled: !!employeeId && !!branchId && !!courseTicketId && pickerOpen,
  });

  const existingMemberIds = new Set(memberRows.map(m => m.memberId));
  const filteredCandidates = candidates
    .filter(c => !existingMemberIds.has(c.memberId))
    .filter(c => !memberSearch || c.memberName?.includes(memberSearch) || c.phoneNumber?.includes(memberSearch));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <LessonLabel sx={{ width: 'auto' }}>{t('회원별 단위 설정')}</LessonLabel>
        <AddRangeButton onClick={() => setPickerOpen(o => !o)}>
          <AddCircleOutlineIcon sx={{ fontSize: 20, color: '#4AB3BC' }} />
          <AddRangeText sx={{ fontSize: '14px' }}>{t('회원 추가')}</AddRangeText>
        </AddRangeButton>
      </Box>

      {pickerOpen && (
        <Box sx={{ border: '1px solid #D9D9D9', borderRadius: '8px', padding: '10px', backgroundColor: '#fff' }}>
          <SearchField sx={{ width: '100%', mb: '8px' }}>
            <RealInput
              inputWidth="100%"
              textAlign="left"
              placeholder={t('이름 또는 전화번호로 검색')}
              value={memberSearch}
              onChange={e => setMemberSearch(e.target.value)}
              style={{ border: 'none', height: '32px', fontSize: '14px' }}
            />
            <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
          </SearchField>
          <Box sx={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {filteredCandidates.length === 0 && (
              <EmptyRangeHint sx={{ padding: '10px 4px' }}>
                {t('이 강사가 담당하는, 이 상품을 보유한 회원이 없습니다')}
              </EmptyRangeHint>
            )}
            {filteredCandidates.map(c => (
              <Box key={c.memberId}
                onClick={() => { onAddMember(c); setPickerOpen(false); setMemberSearch(''); }}
                sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 10px', borderRadius: '6px', cursor: 'pointer',
                  '&:hover': { backgroundColor: '#F5FAFA' },
                }}>
                <TableCellText sx={{ textAlign: 'left', fontWeight: 600 }}>{c.memberName}</TableCellText>
                <InputUnit sx={{ fontSize: '14px' }}>{c.phoneNumber}</InputUnit>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {memberRows.length === 0 && !pickerOpen && (
        <EmptyRangeHint>{t('적용된 회원별 단위 설정이 없습니다. 필요하면 위에서 추가해주세요.')}</EmptyRangeHint>
      )}

      {memberRows.map(row => (
        <Box key={row.memberId} sx={{
          border: '1px solid #E8EDED', borderRadius: '8px', padding: '14px',
          backgroundColor: '#fff', display: 'flex', flexDirection: 'column', gap: '10px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>{row.memberName || row.memberId}</Box>
            <RemoveCircleOutlineIcon
              sx={{ fontSize: 20, color: '#CF1322', cursor: 'pointer' }}
              onClick={() => onRemoveMember(row.memberId)}
            />
          </Box>
          <IndividualTicketFieldsForm item={row} onChange={(field, value) => onFieldChange(row.memberId, field, value)} />
        </Box>
      ))}
    </Box>
  );
};

const IndividualTicketModal = ({ open, onClose, settings, onSettingsChange, employeeId }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const branchId = user.branch?.branchId;
  const [localTickets, setLocalTickets] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [assignedOnly, setAssignedOnly] = useState(false);

  // 모달 열릴 때 로컬 상태 초기화
  React.useEffect(() => {
    if (open) {
      setLocalTickets([...(settings.individualTickets || [])]);
      setSearchText('');
      setExpandedId(null);
      setAssignedOnly(false);
    }
  }, [open]);

  // 상품 단위 행(memberId 없음)이 있는 상품 id만 — "개별" 배지·+/- 아이콘은 상품 단위 행 기준이다.
  const productLevelIds = new Set(localTickets.filter(t => !t.memberId).map(t => t.courseTicketId));

  const { data: ticketData } = useQuery({
    queryKey: ['privateTickets', branchId],
    queryFn: () => getAllTickets(`branchId=${branchId}&classTypes=PRIVATE_LESSON&limit=100`, user.token),
    enabled: !!branchId && open,
  });

  const { data: assignedTicketIds } = useQuery({
    queryKey: ['trainerAssignedTicketIds', employeeId, branchId],
    queryFn: () => getTrainerAssignedTicketIds(employeeId, branchId, user.token),
    enabled: !!employeeId && !!branchId && open && assignedOnly,
  });
  const assignedIdSet = new Set(assignedTicketIds || []);

  const allTickets = ticketData?.content || ticketData || [];
  const filteredTickets = allTickets
    .filter(t => !searchText || t.ticketName?.includes(searchText))
    .filter(t => !assignedOnly || assignedIdSet.has(t.id));

  const handleAddTicket = ticket => {
    setLocalTickets(prev => [...prev, {
      courseTicketId: ticket.id, memberId: null, ...INDIVIDUAL_TICKET_DEFAULTS,
    }]);
    setExpandedId(ticket.id);
  };

  // 상품 단위 행만 제거한다 — 같은 상품의 회원 단위 예외는 독립적이라 그대로 남는다.
  const handleRemoveProductTicket = ticketId => {
    setLocalTickets(prev => prev.filter(t => !(t.courseTicketId === ticketId && !t.memberId)));
  };

  const handleFieldChange = (ticketId, memberId, field, value) => {
    setLocalTickets(prev => prev.map(t =>
      t.courseTicketId === ticketId && (t.memberId || null) === (memberId || null)
        ? { ...t, [field]: value } : t
    ));
  };

  const handleAddMemberTicket = (ticketId, candidate) => {
    setLocalTickets(prev => [...prev, {
      courseTicketId: ticketId, memberId: candidate.memberId, memberName: candidate.memberName,
      ...INDIVIDUAL_TICKET_DEFAULTS,
    }]);
  };

  const handleRemoveMemberTicket = (ticketId, memberId) => {
    setLocalTickets(prev => prev.filter(t => !(t.courseTicketId === ticketId && t.memberId === memberId)));
  };

  const handleSave = () => {
    onSettingsChange('individualTickets', localTickets);
    onClose();
  };

  const formatPrice = val => (val || 0).toLocaleString('ko-KR') + '원';

  const ticketColumns = {
    name: { flex: 1 },
    sale: { cellWidth: '130px' },
    count: { cellWidth: '80px' },
    unit: { cellWidth: '130px' },
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '920px', height: '78vh', bgcolor: '#fff', borderRadius: '12px',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
      }}>
        {/* 헤더 — 이 프로젝트의 모달 헤더 컨벤션(#262626 + 흰 텍스트)을 따른다 */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0 28px', height: '56px', flexShrink: 0,
          backgroundColor: '#262626',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box sx={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>
              {t('개인레슨 개별 수당 설정')}
            </Box>
            <IndividualBadge>{localTickets.length}{t('개 적용 중')}</IndividualBadge>
          </Box>
          <CloseIcon sx={{ cursor: 'pointer', color: '#fff', fontSize: 22 }} onClick={onClose} />
        </Box>

        {/* 검색 */}
        <Box sx={{ padding: '16px 28px', borderBottom: '1px solid #F2F2F2', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <SearchField sx={{ width: '320px' }}>
            <RealInput
              inputWidth="100%"
              textAlign="left"
              placeholder={t('수강권 검색')}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ border: 'none', height: '38px', fontSize: '15px' }}
            />
            <SearchIcon sx={{ fontSize: 20, color: '#999' }} />
          </SearchField>
          <CheckRow onClick={() => setAssignedOnly(prev => !prev)} sx={{ cursor: 'pointer' }}>
            <CheckboxCircle isChecked={assignedOnly} sx={{ width: '20px', height: '20px' }}>
              {assignedOnly && <CheckIcon sx={{ fontSize: 14 }} />}
            </CheckboxCircle>
            <CheckLabel sx={{ fontSize: '14px' }}>{t('담당중인 수강권만 보기')}</CheckLabel>
          </CheckRow>
        </Box>

        {/* 테이블 */}
        <Box sx={{ flex: 1, overflowY: 'auto', padding: '0 28px 20px' }}>
          <TableWrapper sx={{ mt: '16px' }}>
            <TableHeaderRow>
              <TableHeaderCell flex={ticketColumns.name.flex} sx={{ justifyContent: 'flex-start' }}>
                <TableHeaderText>{t('수강권 명')}</TableHeaderText>
              </TableHeaderCell>
              <TableHeaderCell cellWidth={ticketColumns.sale.cellWidth}>
                <TableHeaderText>{t('판매금액')}</TableHeaderText>
              </TableHeaderCell>
              <TableHeaderCell cellWidth={ticketColumns.count.cellWidth}>
                <TableHeaderText>{t('횟수')}</TableHeaderText>
              </TableHeaderCell>
              <TableHeaderCell cellWidth={ticketColumns.unit.cellWidth}>
                <TableHeaderText>{t('회당 가격')}</TableHeaderText>
              </TableHeaderCell>
            </TableHeaderRow>

            {filteredTickets.length === 0 && (
              <EmptyRangeHint sx={{ textAlign: 'center', padding: '24px 0' }}>
                {t('검색 결과가 없습니다')}
              </EmptyRangeHint>
            )}

            {filteredTickets.map(ticket => {
              const hasProductSetting = productLevelIds.has(ticket.id);
              const productTicket = localTickets.find(t => t.courseTicketId === ticket.id && !t.memberId);
              const memberRows = localTickets.filter(t => t.courseTicketId === ticket.id && t.memberId);
              const hasAnySetting = hasProductSetting || memberRows.length > 0;
              const isExpanded = expandedId === ticket.id;
              const unitPrice = ticket.usageCount > 0 ? Math.round(ticket.finalPrice / ticket.usageCount) : 0;

              return (
                <TableBodyRow
                  key={ticket.id}
                  isExpanded={isExpanded}
                  sx={{
                    backgroundColor: isExpanded ? '#F3F9FA' : '#fff',
                    '&:hover': { backgroundColor: isExpanded ? '#F3F9FA' : '#FAFAFA' },
                    transition: 'background-color 0.15s',
                  }}
                >
                  {/* 수강권 행 — 상품/회원 단위 설정이 없어도 눌러서 펼칠 수 있다(회원 단위만 걸 수도 있음) */}
                  <TableBodyCellRow sx={{ cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : ticket.id)}>
                    <TableBodyCell flex={ticketColumns.name.flex} sx={{ justifyContent: 'flex-start', gap: '8px' }}>
                      <TableCellText sx={{ textAlign: 'left', fontWeight: hasAnySetting ? 600 : 500 }}>
                        {ticket.ticketName}
                      </TableCellText>
                      {hasAnySetting && <IndividualBadge sx={{ marginLeft: 0, padding: '2px 8px', fontSize: '12px' }}>{t('개별')}</IndividualBadge>}
                    </TableBodyCell>
                    <TableBodyCell cellWidth={ticketColumns.sale.cellWidth}>
                      <TableCellText sx={{ textAlign: 'right', color: '#666' }}>{formatPrice(ticket.finalPrice)}</TableCellText>
                    </TableBodyCell>
                    <TableBodyCell cellWidth={ticketColumns.count.cellWidth}>
                      <TableCellText sx={{ color: '#666' }}>{ticket.usageCount}{t('회')}</TableCellText>
                    </TableBodyCell>
                    <TableBodyCell cellWidth={ticketColumns.unit.cellWidth}>
                      <TableCellText sx={{ textAlign: 'right', color: '#666' }}>{formatPrice(unitPrice)}</TableCellText>
                    </TableBodyCell>
                  </TableBodyCellRow>

                  {/* 펼쳐지는 상세 설정 — 상품 단위 폼(있을 때만) + 회원별 예외(항상) */}
                  {isExpanded && (
                    <Box sx={{
                      borderTop: '1px solid #E8EDED', padding: '18px 24px 18px 40px',
                      display: 'flex', flexDirection: 'column', gap: '12px',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <LessonLabel sx={{ width: 'auto' }}>{t('상품 단위 설정')}</LessonLabel>
                        {productTicket ? (
                          <Box onClick={() => handleRemoveProductTicket(ticket.id)}
                            sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                            <RemoveCircleOutlineIcon sx={{ fontSize: 20, color: '#CF1322' }} />
                            <RadioLabel sx={{ color: '#CF1322', fontSize: '14px' }}>{t('설정 제거')}</RadioLabel>
                          </Box>
                        ) : (
                          <AddRangeButton onClick={() => handleAddTicket(ticket)}>
                            <AddCircleOutlineIcon sx={{ fontSize: 20, color: '#4AB3BC' }} />
                            <AddRangeText sx={{ fontSize: '14px' }}>{t('상품 단위 설정 추가')}</AddRangeText>
                          </AddRangeButton>
                        )}
                      </Box>

                      {productTicket ? (
                        <IndividualTicketFieldsForm
                          item={productTicket}
                          onChange={(field, value) => handleFieldChange(ticket.id, null, field, value)}
                        />
                      ) : (
                        <EmptyRangeHint>
                          {t('적용된 상품 단위 설정이 없습니다. 필요하면 위에서 추가해주세요.')}
                        </EmptyRangeHint>
                      )}

                      <Divider />

                      <MemberExceptionSection
                        courseTicketId={ticket.id}
                        employeeId={employeeId}
                        branchId={branchId}
                        token={user.token}
                        memberRows={memberRows}
                        onAddMember={candidate => handleAddMemberTicket(ticket.id, candidate)}
                        onRemoveMember={memberId => handleRemoveMemberTicket(ticket.id, memberId)}
                        onFieldChange={(memberId, field, value) => handleFieldChange(ticket.id, memberId, field, value)}
                      />
                    </Box>
                  )}
                </TableBodyRow>
              );
            })}
          </TableWrapper>
        </Box>

        {/* 푸터 */}
        <Box sx={{
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
          padding: '16px 28px', borderTop: '1px solid #E8E8E8',
        }}>
          <CommonModalButton variant="cancel" onClick={onClose}>{t('취소')}</CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSave}>{t('저장')}</CommonModalButton>
        </Box>
      </Box>
    </Modal>
  );
};

// ============================================
// 개인레슨 개별 수당 (요약 + 모달 열기)
// ============================================
const PrivateLessonIndividualSection = ({ settings, onSettingsChange, employeeId }) => {
  const { t } = useTranslation();
  const enabled = settings.privateLessonIndividualEnabled === 'Y';
  const individualTickets = settings.individualTickets || [];
  const productCount = individualTickets.filter(t => !t.memberId).length;
  const memberCount = individualTickets.filter(t => t.memberId).length;
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <FieldBlock>
      <FormRow sx={{ alignItems: 'flex-start' }}>
        <FormFieldGroup>
          <ToggleFieldRow>
            <FormFieldLabel>{t('개인레슨 개별 수당')}</FormFieldLabel>
            <ToggleSwitch
              checked={enabled}
              onChange={() => onSettingsChange('privateLessonIndividualEnabled', enabled ? 'N' : 'Y')}
            />
          </ToggleFieldRow>
          <FormFieldDesc>{t('설정한 수강권만 기본 수당 대신 개별 요율 적용')}</FormFieldDesc>
        </FormFieldGroup>

        {enabled && (
          <FieldBlock sx={{ flex: 1, gap: '12px' }}>
            <IndividualHeader>
              <IndividualHeaderText>
                <strong>
                  {productCount}{t('개 수강권')}
                  {memberCount > 0 && ` + ${memberCount}${t('명 회원별 단위 설정')}`}
                </strong>
              </IndividualHeaderText>
              <CommonModalButton variant="submit" onClick={() => setModalOpen(true)}>
                {t('수강권 설정')}
              </CommonModalButton>
            </IndividualHeader>
          </FieldBlock>
        )}
      </FormRow>

      <IndividualTicketModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        settings={settings}
        onSettingsChange={onSettingsChange}
        employeeId={employeeId}
      />
    </FieldBlock>
  );
};

// ============================================
// 그룹레슨 기본 수당
// ============================================
const GroupLessonSection = ({ settings, onSettingsChange, validationError }) => {
  const { t } = useTranslation();
  const enabled = settings.groupLessonEnabled === 'Y';
  const ranges = settings.groupRanges || [];

  const handleAddRange = () => {
    const lastEnd = ranges.length > 0 ? ranges[ranges.length - 1].rangeEnd : -1;
    const newRange = {
      rangeStart: lastEnd + 1,
      rangeEnd: lastEnd + 5,
      amount: 0,
      unit: '원',
      rangeOrder: ranges.length,
    };
    onSettingsChange('groupRanges', [...ranges, newRange]);
  };

  const handleRemoveRange = idx => {
    const updated = ranges.filter((_, i) => i !== idx)
      .map((r, i) => ({ ...r, rangeOrder: i }));
    onSettingsChange('groupRanges', updated);
  };

  const handleRangeChange = (idx, field, value) => {
    const updated = [...ranges];
    updated[idx] = { ...updated[idx], [field]: value };
    onSettingsChange('groupRanges', updated);
  };

  return (
    <FieldBlock>
      <FormRow sx={{ alignItems: 'flex-start' }}>
        <FormFieldGroup>
          <ToggleFieldRow>
            <FormFieldLabel>{t('그룹레슨 기본 수당')}</FormFieldLabel>
            <ToggleSwitch
              checked={enabled}
              onChange={() => onSettingsChange('groupLessonEnabled', enabled ? 'N' : 'Y')}
            />
          </ToggleFieldRow>
          <FormFieldDesc>{t('그룹레슨 수업당 강사에게 지급되는 수당')}</FormFieldDesc>
        </FormFieldGroup>

        {enabled && (
          <FieldBlock sx={{ flex: 1, gap: '16px' }}>
            {/* 인원 기준 */}
            <RadioRow sx={{ justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LessonLabel required>{t('인원 기준')}</LessonLabel>
                <HelpTooltip title={"수당 구간 산정에 사용할 인원 기준입니다.\n\n출석만: 실제 출석한 인원만 카운트\n출석+결석: 출석 인원 + 결석 인원을 합산\n출석+차감: 출석 + 차감된 결석을 합산"} />
              </Box>
              {['ATTEND_ONLY', 'ATTEND_ABSENT', 'ATTEND_DEDUCTED'].map(key => (
                <RadioItem key={key} onClick={() => onSettingsChange('groupLessonCountBasis', key)}>
                  <RadioCircle isSelected={settings.groupLessonCountBasis === key}>
                    {settings.groupLessonCountBasis === key && <RadioDot />}
                  </RadioCircle>
                  <RadioLabel>
                    {key === 'ATTEND_ONLY' ? t('출석만') : key === 'ATTEND_ABSENT' ? t('출석+결석') : t('출석+차감된 결석')}
                  </RadioLabel>
                </RadioItem>
              ))}
            </RadioRow>

            <Divider />

            {/* 구간 설정 */}
            <Box data-field="groupRanges" sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {ranges.length === 0 && (
                <EmptyRangeHint>{t('설정된 구간이 없습니다. 구간을 추가해주세요')}</EmptyRangeHint>
              )}
              {ranges.map((range, idx) => {
                const isFirstEmpty = validationError?.field === 'groupRangeAmount' && !range.amount && ranges.findIndex(r => !r.amount) === idx;
                return (
                  <Box key={idx}>
                    <RangeRow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: '160px' }}>
                        <RealInput
                          inputWidth="60px"
                          placeholder="0"
                          value={range.rangeStart ?? ''}
                          onChange={e => handleRangeChange(idx, 'rangeStart', Number(e.target.value.replace(/[^0-9]/g, '')) || 0)}
                          style={{ height: '40px', padding: '8px' }}
                        />
                        <RangeSeparator>~</RangeSeparator>
                        <RealInput
                          inputWidth="60px"
                          placeholder="0"
                          value={range.rangeEnd || ''}
                          onChange={e => handleRangeChange(idx, 'rangeEnd', Number(e.target.value.replace(/[^0-9]/g, '')) || 0)}
                          style={{ height: '40px', padding: '8px' }}
                        />
                      </Box>
                      <InputUnit>{t('명 까지')}</InputUnit>
                      <ErrorFieldWrapper hasError={isFirstEmpty} data-field="groupRangeAmount">
                        <CommaInput
                          inputWidth="140px"
                          value={range.amount || 0}
                          onChange={val => handleRangeChange(idx, 'amount', val)}
                        />
                      </ErrorFieldWrapper>
                      <UnitSelect value={range.unit || '원'} onChange={e => handleRangeChange(idx, 'unit', e.target.value)}>
                        <option value="원">원</option>
                        <option value="%">%</option>
                      </UnitSelect>
                      <RemoveCircleOutlineIcon
                        sx={{ fontSize: 22, color: '#CF1322', cursor: 'pointer', flexShrink: 0 }}
                        onClick={() => handleRemoveRange(idx)}
                      />
                    </RangeRow>
                    {isFirstEmpty && <ErrorText>{t('구간 수당을 입력해주세요')}</ErrorText>}
                  </Box>
                );
              })}

              <AddRangeButton onClick={handleAddRange}>
                <AddCircleOutlineIcon sx={{ fontSize: 24, color: '#4AB3BC' }} />
                <AddRangeText>{t('구간 추가')}</AddRangeText>
              </AddRangeButton>

              <NoteText>
                {t('※ 퍼센트로 설정 시 개인레슨과 달리 "기본 급여액"에서 설정한 퍼센트만큼 산정됩니다')}
                <br />
                {t('※ 설정한 구간의 상한을 넘는 인원은 마지막 구간 수당이 적용됩니다')}
              </NoteText>
            </Box>
          </FieldBlock>
        )}
      </FormRow>
    </FieldBlock>
  );
};

// ============================================
// 메인
// ============================================
const SalarySettingLesson = ({ settings, onSettingsChange, validationError, employee }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <SectionTitleRow>
        <SectionTitle>{t('수업 수당 설정')}</SectionTitle>
      </SectionTitleRow>

      <SectionContent sx={{ mt: '24px' }}>
        <PrivateLessonBasicSection settings={settings} onSettingsChange={onSettingsChange} validationError={validationError} />
        <Divider />
        <PrivateLessonIndividualSection settings={settings} onSettingsChange={onSettingsChange} employeeId={employee?.employeeId} />
        <Divider />
        <GroupLessonSection settings={settings} onSettingsChange={onSettingsChange} validationError={validationError} />
      </SectionContent>
    </Card>
  );
};

export default SalarySettingLesson;
